/**
 * Stage 20 · Global Operations Control Tower.
 *
 * The first screen Suntronix Operations opens every morning.
 * Every count is drawn from live platform data.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, Building2, Compass, Globe2, Layers, LineChart,
  Radar, RefreshCw, Rocket, Shield, ShieldAlert, ShieldCheck, Truck, Undo2,
  Wrench, Warehouse as WarehouseIcon, Zap, Plug, Siren, Plus, Link2, Filter,
  ChevronDown, ChevronRight, Clock,
} from "lucide-react";

const REFRESH_MS = 30_000;

const TABS = [
  { key: "cockpit",       label: "Cockpit",         icon: Activity },
  { key: "ask",           label: "Ask ONE",         icon: Compass },
  { key: "action",        label: "Action centre",   icon: ShieldAlert },
  { key: "customers",     label: "Customers",       icon: Building2 },
  { key: "countries",     label: "Countries",       icon: Globe2 },
  { key: "sla",           label: "SLA command",     icon: LineChart },
  { key: "timeline",      label: "Timeline",        icon: Clock },
  { key: "queues",        label: "Control queues",  icon: Layers },
  { key: "integrations",  label: "Integrations",    icon: Plug },
  { key: "incidents",     label: "Incidents",       icon: Siren },
  { key: "alerts",        label: "Alert rules",     icon: Radar },
];

export default function ControlTower() {
  const [tab, setTab] = useState("cockpit");
  const [masters, setMasters] = useState([]);
  const [countries, setCountries] = useState([]);
  const [mc, setMc] = useState("");
  const [country, setCountry] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    api.get("/master-companies").then(({ data }) => setMasters(data.items || [])).catch(() => {});
    api.get("/control-tower/countries").then(({ data }) => setCountries(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => setRefreshTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const scope = useMemo(() => ({ master_company_id: mc || undefined, country: country || undefined }), [mc, country]);

  return (
    <div className="max-w-[1200px] space-y-5" data-testid="control-tower">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Operations · Stage 20
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">
            Global Operations Control Tower
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Every customer · country · asset · request · deployment · shipment · recovery · warehouse
            transaction · service case · SLA. One cockpit. Verified data only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={"h-9 px-3 rounded-md border text-sm inline-flex items-center gap-1.5 " +
              (autoRefresh ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-slate-200 text-slate-600")}
            data-testid="ct-auto-refresh">
            <Zap className="h-4 w-4" /> Auto refresh {autoRefresh ? "on" : "off"}
          </button>
          <button onClick={() => setRefreshTick((t) => t + 1)} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm inline-flex items-center gap-1.5" data-testid="ct-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          {lastUpdated && (
            <span className="text-[11px] text-slate-500">Updated {new Date(lastUpdated).toLocaleTimeString()}</span>
          )}
        </div>
      </div>

      <FilterBar
        masters={masters} countries={countries}
        mc={mc} setMc={setMc}
        country={country} setCountry={setCountry}
      />

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key} role="tab" aria-selected={tab === key}
            data-testid={`ct-tab-${key}`}
            onClick={() => setTab(key)}
            className={
              "px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors " +
              (tab === key
                ? "border-slate-900 text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-800")
            }>
            <Icon className="h-4 w-4" strokeWidth={1.5} /> {label}
          </button>
        ))}
      </nav>

      <div className="pt-1">
        {tab === "cockpit"      && <Cockpit scope={scope} tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "ask"          && <AskOne scope={scope} />}
        {tab === "action"       && <ActionCentre scope={scope} tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "customers"    && <CustomersBoard tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "countries"    && <CountriesBoard tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "sla"          && <SlaCommand scope={scope} tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "timeline"     && <TimelineTab scope={scope} tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "queues"       && <QueuesTab scope={scope} tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "integrations" && <IntegrationsTab tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "incidents"    && <IncidentsTab masters={masters} tick={refreshTick} onLoaded={setLastUpdated} />}
        {tab === "alerts"       && <AlertRulesTab tick={refreshTick} onLoaded={setLastUpdated} />}
      </div>
    </div>
  );
}

/* ============================================================ Filter Bar */
function FilterBar({ masters, countries, mc, setMc, country, setCountry }) {
  const clear = () => { setMc(""); setCountry(""); };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="ct-filter-bar">
      <Filter className="h-4 w-4 text-slate-500" />
      <label>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Customer</div>
        <select className="input min-w-[220px]" value={mc} onChange={(e) => setMc(e.target.value)} data-testid="ct-filter-customer">
          <option value="">All customers</option>
          {masters.map((m) => <option key={m.id} value={m.id}>{m.name}{m.is_demo ? " · DEMO" : ""}</option>)}
        </select>
      </label>
      <label>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Country</div>
        <select className="input min-w-[160px]" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="ct-filter-country">
          <option value="">All countries</option>
          {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </label>
      {(mc || country) && (
        <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-900 mt-4" data-testid="ct-filter-clear">Clear</button>
      )}
    </div>
  );
}

/* ============================================================ Cockpit */
function Cockpit({ scope, tick, onLoaded }) {
  const [tiles, setTiles] = useState([]);
  const [pulse, setPulse] = useState([]);
  useEffect(() => {
    api.get("/control-tower/overview", { params: scope }).then(({ data }) => { setTiles(data.tiles || []); onLoaded?.(data.generated_at); }).catch(() => {});
    api.get("/control-tower/pulse").then(({ data }) => setPulse(data.items || [])).catch(() => {});
  }, [scope.master_company_id, scope.country, tick, scope, onLoaded]);

  return (
    <div className="space-y-4" data-testid="ct-cockpit">
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map((t) => <Tile key={t.key} label={t.label} value={t.value} testId={`ct-tile-${t.key}`} />)}
      </section>
      <section className="bg-white border border-slate-200 rounded-md">
        <div className="p-3 border-b border-slate-200 flex items-center gap-2">
          <Activity className="h-4 w-4 text-slate-500" />
          <h2 className="text-sm font-medium text-slate-900">Live operating pulse</h2>
          <span className="ml-auto text-[10px] text-slate-500">Near real-time · Refreshes every {REFRESH_MS/1000}s</span>
        </div>
        <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-3">
          {pulse.map((p) => <PulseRow key={p.key} label={p.label} value={p.value} testId={`ct-pulse-${p.key}`} />)}
        </div>
      </section>
    </div>
  );
}

function Tile({ label, value, testId }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-heading font-semibold tabular-nums text-slate-900">{value ?? "—"}</div>
    </div>
  );
}
function PulseRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2" data-testid={testId}>
      <span className="text-sm text-slate-700">{label}</span>
      <span className="text-lg tabular-nums font-semibold text-slate-900">{value ?? "—"}</span>
    </div>
  );
}

/* ============================================================ Action Centre */
function ActionCentre({ scope, tick, onLoaded }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/control-tower/action-centre", { params: scope }).then(({ data }) => { setData(data); onLoaded?.(data.generated_at); }).catch(() => setData(null));
  }, [onLoaded, scope, scope.master_company_id, tick]);

  if (!data) return <Skeleton />;
  return (
    <div className="space-y-3" data-testid="ct-action-centre">
      {data.groups.map((g) => (
        <section key={g.key} className="bg-white border border-slate-200 rounded-md">
          <div className="p-3 border-b border-slate-200 flex items-center gap-2">
            <ShieldAlert className={"h-4 w-4 " + toneColor(g.key)} />
            <h2 className="text-sm font-medium text-slate-900">{g.label}</h2>
            <span className="ml-auto text-xs text-slate-500 tabular-nums">
              {g.items.reduce((s, i) => s + (i.value || 0), 0)} total
            </span>
          </div>
          <div className="p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            {g.items.map((it) => (
              <div key={it.key} className={"rounded-md border p-3 " + toneBg(g.key)} data-testid={`ct-action-${g.key}-${it.key}`}>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{it.label}</div>
                <div className="mt-1 text-2xl font-heading font-semibold tabular-nums text-slate-900">{it.value ?? 0}</div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
function toneColor(k) { return k === "critical" ? "text-rose-600" : k === "overdue" ? "text-amber-600" : k === "today" ? "text-sky-600" : k === "blocked" ? "text-slate-600" : "text-orange-600"; }
function toneBg(k)    { return k === "critical" ? "bg-rose-50 border-rose-200" : k === "overdue" ? "bg-amber-50 border-amber-200" : k === "today" ? "bg-sky-50 border-sky-200" : k === "blocked" ? "bg-slate-50 border-slate-200" : "bg-orange-50 border-orange-200"; }

/* ============================================================ Customers */
function CustomersBoard({ tick, onLoaded }) {
  const [items, setItems] = useState([]);
  const [methodology, setMethodology] = useState("");
  useEffect(() => {
    api.get("/control-tower/customers").then(({ data }) => { setItems(data.items || []); setMethodology(data.methodology || ""); onLoaded?.(data.generated_at); }).catch(() => {});
  }, [onLoaded, tick]);
  return (
    <div className="space-y-3" data-testid="ct-customers-board">
      {methodology && <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">{methodology}</div>}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left py-2 px-3">Customer</th>
              <th className="text-right py-2 px-3">Requests</th>
              <th className="text-right py-2 px-3">Deploy</th>
              <th className="text-right py-2 px-3">Recovery</th>
              <th className="text-right py-2 px-3">Service</th>
              <th className="text-right py-2 px-3">Exceptions</th>
              <th className="text-right py-2 px-3">Overdue</th>
              <th className="text-left py-2 px-3">Health</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="py-6 text-center text-slate-500">No customers configured.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`ct-customer-row-${c.code || c.id}`}>
                <td className="py-2 px-3">
                  <div className="text-slate-900 font-medium">{c.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{c.code} · {c.country || "—"}{c.is_demo ? " · DEMO" : ""}</div>
                </td>
                <td className="py-2 px-3 text-right tabular-nums">{c.open_requests}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.deployments}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.recoveries}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.service}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.exceptions}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.overdue_actions}</td>
                <td className="py-2 px-3"><HealthChip health={c.health} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function HealthChip({ health }) {
  const map = {
    healthy:   { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Healthy" },
    attention: { cls: "bg-amber-100 text-amber-800 border-amber-200",       label: "Attention" },
    critical:  { cls: "bg-rose-100 text-rose-800 border-rose-200",          label: "Critical" },
  };
  const c = map[health] || map.healthy;
  return <span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + c.cls}>{c.label}</span>;
}

/* ============================================================ Countries */
function CountriesBoard({ tick, onLoaded }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/control-tower/countries").then(({ data }) => { setItems(data.items || []); onLoaded?.(data.generated_at); }).catch(() => {});
  }, [onLoaded, tick]);
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="ct-countries-board">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
          <tr>
            <th className="text-left py-2 px-3">Country</th>
            <th className="text-right py-2 px-3">Assets</th>
            <th className="text-right py-2 px-3">Requests</th>
            <th className="text-right py-2 px-3">Deploy</th>
            <th className="text-right py-2 px-3">Recovery</th>
            <th className="text-right py-2 px-3">Shipments</th>
            <th className="text-right py-2 px-3">Service</th>
            <th className="text-right py-2 px-3">Critical</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={8} className="py-6 text-center text-slate-500">No countries configured yet.</td></tr>
          ) : items.map((c) => (
            <tr key={c.code} className="border-t border-slate-100" data-testid={`ct-country-row-${c.code}`}>
              <td className="py-2 px-3"><div className="text-slate-900 font-medium">{c.name}</div><div className="text-[10px] font-mono text-slate-500">{c.code}</div></td>
              <td className="py-2 px-3 text-right tabular-nums">{c.assets}</td>
              <td className="py-2 px-3 text-right tabular-nums">{c.requests}</td>
              <td className="py-2 px-3 text-right tabular-nums">{c.deployments}</td>
              <td className="py-2 px-3 text-right tabular-nums">{c.recoveries}</td>
              <td className="py-2 px-3 text-right tabular-nums">{c.shipments}</td>
              <td className="py-2 px-3 text-right tabular-nums">{c.service}</td>
              <td className="py-2 px-3 text-right tabular-nums">
                {c.critical_issues > 0 ? <span className="text-rose-700 font-semibold">{c.critical_issues}</span> : c.critical_issues}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================ SLA */
function SlaCommand({ scope, tick, onLoaded }) {
  const [buckets, setBuckets] = useState([]);
  useEffect(() => {
    api.get("/control-tower/sla", { params: scope }).then(({ data }) => { setBuckets(data.buckets || []); onLoaded?.(data.generated_at); }).catch(() => {});
  }, [onLoaded, scope, scope.master_company_id, tick]);
  return (
    <div className="space-y-3" data-testid="ct-sla-command">
      {buckets.map((b) => (
        <section key={b.key} className="bg-white border border-slate-200 rounded-md">
          <div className={"p-3 border-b border-slate-200 flex items-center gap-2 " + slaHeader(b.key)}>
            <LineChart className="h-4 w-4" />
            <h2 className="text-sm font-medium">{b.label}</h2>
            <span className="ml-auto text-xs tabular-nums">{b.items.length}</span>
          </div>
          {b.items.length === 0 ? (
            <div className="p-4 text-sm text-slate-500">Nothing here.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3">Item</th>
                  <th className="text-left py-2 px-3">Owner</th>
                  <th className="text-left py-2 px-3">State</th>
                  <th className="text-right py-2 px-3">Due</th>
                </tr>
              </thead>
              <tbody>
                {b.items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100">
                    <td className="py-2 px-3 text-slate-900">{it.title}</td>
                    <td className="py-2 px-3 text-slate-600">{it.owner}</td>
                    <td className="py-2 px-3 text-slate-600">{it.state}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-slate-700">{it.due_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ))}
    </div>
  );
}
function slaHeader(k) {
  return k === "breached" ? "bg-rose-50 text-rose-900" :
         k === "today"    ? "bg-amber-50 text-amber-900" :
         k === "at_risk"  ? "bg-orange-50 text-orange-900" :
         "bg-emerald-50 text-emerald-900";
}

/* ============================================================ Timeline */
function TimelineTab({ scope, tick, onLoaded }) {
  const [today, setToday] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  useEffect(() => {
    api.get("/control-tower/timeline/today", { params: scope }).then(({ data }) => { setToday(data); onLoaded?.(data.generated_at); }).catch(() => {});
    api.get("/control-tower/timeline/upcoming", { params: { ...scope, days: 7 } }).then(({ data }) => setUpcoming(data)).catch(() => {});
  }, [onLoaded, scope, scope.master_company_id, tick]);
  return (
    <div className="space-y-4" data-testid="ct-timeline">
      <section className="bg-white border border-slate-200 rounded-md">
        <div className="p-3 border-b border-slate-200 flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-500" />
          <h2 className="text-sm font-medium text-slate-900">Today</h2>
          <span className="ml-auto text-[10px] text-slate-500 tabular-nums">{today?.events?.length ?? 0} events</span>
        </div>
        <div className="divide-y">
          {(today?.events || []).length === 0 ? (
            <div className="p-4 text-sm text-slate-500">No events scheduled today.</div>
          ) : (today.events).map((e, i) => (
            <div key={i} className="p-3 flex items-center gap-3">
              <span className="text-[10px] font-mono w-16 text-slate-500 tabular-nums">{(e.time || "").slice(11, 16)}</span>
              <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + eventChip(e.kind)}>{e.kind}</span>
              <span className="text-sm text-slate-900">{e.detail}</span>
              <span className="ml-auto text-[10px] text-slate-500 font-mono">{e.ref}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-md p-4">
        <h2 className="text-sm font-medium text-slate-900 mb-2">Next 7 days</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {(upcoming?.counters || []).map((c) => <PulseRow key={c.key} label={c.label} value={c.value} testId={`ct-upcoming-${c.key}`} />)}
        </div>
      </section>
    </div>
  );
}
function eventChip(k) {
  return k === "delivery" ? "bg-sky-50 border-sky-200 text-sky-800" :
         k === "pickup"   ? "bg-amber-50 border-amber-200 text-amber-800" :
         k === "order"    ? "bg-slate-100 border-slate-200 text-slate-800" :
                             "bg-emerald-50 border-emerald-200 text-emerald-800";
}

/* ============================================================ Queues */
function QueuesTab({ scope, tick, onLoaded }) {
  const [dep, setDep]     = useState([]);
  const [rec, setRec]     = useState([]);
  const [logi, setLogi]   = useState(null);
  const [wh, setWh]       = useState([]);
  const [svc, setSvc]     = useState([]);
  useEffect(() => {
    const params = scope;
    Promise.all([
      api.get("/control-tower/queue/deployments", { params }),
      api.get("/control-tower/queue/recoveries",  { params }),
      api.get("/control-tower/queue/logistics",   { params }),
      api.get("/control-tower/queue/warehouse",   { params }),
      api.get("/control-tower/queue/service",     { params }),
    ]).then(([d, r, l, w, s]) => {
      setDep(d.data.stages || []); setRec(r.data.stages || []);
      setLogi(l.data); setWh(w.data.stages || []); setSvc(s.data.stages || []);
      onLoaded?.(new Date().toISOString());
    }).catch(() => {});
  }, [onLoaded, scope, scope.master_company_id, tick]);

  return (
    <div className="space-y-4" data-testid="ct-queues">
      <StagePanel title="Deployments"  icon={Rocket}         stages={dep} testId="ct-queue-deployments" />
      <StagePanel title="Recoveries"   icon={Undo2}          stages={rec} testId="ct-queue-recoveries" />
      {logi && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <StagePanel title="Logistics · forward" icon={Truck}          stages={logi.forward} testId="ct-queue-logistics-fwd" />
          <StagePanel title="Logistics · reverse" icon={Undo2}          stages={logi.reverse} testId="ct-queue-logistics-rev" />
        </div>
      )}
      <StagePanel title="Warehouse"    icon={WarehouseIcon}  stages={wh}  testId="ct-queue-warehouse" />
      <StagePanel title="Service"      icon={Wrench}         stages={svc} testId="ct-queue-service" />
    </div>
  );
}
function StagePanel({ title, icon: Icon, stages, testId }) {
  const total = (stages || []).reduce((s, x) => s + (x.count || 0), 0);
  return (
    <section className="bg-white border border-slate-200 rounded-md" data-testid={testId}>
      <div className="p-3 border-b border-slate-200 flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h2 className="text-sm font-medium text-slate-900">{title}</h2>
        <span className="ml-auto text-xs text-slate-500 tabular-nums">{total} total</span>
      </div>
      <div className="p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {(stages || []).map((s) => (
          <div key={s.stage} className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 truncate">{s.stage?.replace(/_/g, " ")}</div>
            <div className="text-lg tabular-nums font-semibold text-slate-900">{s.count}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============================================================ Integrations */
function IntegrationsTab({ tick, onLoaded }) {
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState({});
  useEffect(() => {
    api.get("/control-tower/integrations").then(({ data }) => { setItems(data.items || []); setGrouped(data.grouped || {}); onLoaded?.(data.generated_at); }).catch(() => {});
  }, [onLoaded, tick]);
  const ORDER = ["failed", "auth_required", "degraded", "stale", "healthy", "not_configured"];
  return (
    <div className="space-y-3" data-testid="ct-integrations-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {ORDER.map((s) => (
          <div key={s} className={"rounded-md border p-3 " + intBg(s)}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{s.replace(/_/g, " ")}</div>
            <div className="text-2xl font-heading font-semibold tabular-nums text-slate-900 mt-1">{(grouped[s] || []).length}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Integration</th><th className="text-left py-2 px-3">Status</th><th className="text-left py-2 px-3">Notes</th></tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id || i} className="border-t border-slate-100">
                <td className="py-2 px-3 text-slate-900">{it.name}</td>
                <td className="py-2 px-3"><span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + intChip(it.status)}>{it.status}</span></td>
                <td className="py-2 px-3 text-slate-500 text-xs">{it.last_error || it.notes || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function intBg(s) {
  return s === "failed" ? "bg-rose-50 border-rose-200" :
         s === "auth_required" ? "bg-orange-50 border-orange-200" :
         s === "degraded" ? "bg-amber-50 border-amber-200" :
         s === "stale" ? "bg-slate-100 border-slate-200" :
         s === "healthy" ? "bg-emerald-50 border-emerald-200" :
         "bg-white border-slate-200";
}
function intChip(s) {
  return s === "failed" ? "bg-rose-100 text-rose-800 border-rose-200" :
         s === "auth_required" ? "bg-orange-100 text-orange-800 border-orange-200" :
         s === "degraded" ? "bg-amber-100 text-amber-800 border-amber-200" :
         s === "healthy" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
         "bg-slate-100 text-slate-700 border-slate-200";
}

/* ============================================================ Incidents */
function IncidentsTab({ masters, tick, onLoaded }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => api.get("/control-tower/incidents").then(({ data }) => { setItems(data.items || []); onLoaded?.(new Date().toISOString()); }), [onLoaded]);
  useEffect(() => { load().catch(() => {}); }, [load, tick]);

  return (
    <div className="space-y-3" data-testid="ct-incidents-tab">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="ct-incident-new">
          <Plus className="h-4 w-4" /> New incident
        </button>
      </div>
      {showForm && <IncidentForm masters={masters} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md divide-y" data-testid="ct-incidents-list">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No incidents open.</div>
        ) : items.map((inc) => (
          <IncidentRow key={inc.id} inc={inc} expanded={expanded === inc.id} onToggle={() => setExpanded(expanded === inc.id ? null : inc.id)} onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function IncidentForm({ masters, onCancel, onCreated }) {
  const [f, setF] = useState({ title: "", kind: "courier_outage", severity: "high", owner: "", mitigation: "", customer_impact: "", affected_master_company_ids: [] });
  const save = async () => {
    try {
      const { data } = await api.post("/control-tower/incidents", f);
      toast.success(`${data.incident_number} opened`);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="ct-incident-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Title</div>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ct-incident-title" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="ct-incident-kind">
            <option value="courier_outage">Courier outage</option>
            <option value="warehouse_issue">Warehouse issue</option>
            <option value="integration_failure">Integration failure</option>
            <option value="weather">Weather / regional</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Severity</div>
          <select className="input" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} data-testid="ct-incident-severity">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Owner</div>
          <input className="input" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} data-testid="ct-incident-owner" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Affected customer</div>
          <select className="input" value={f.affected_master_company_ids[0] || ""}
            onChange={(e) => setF({ ...f, affected_master_company_ids: e.target.value ? [e.target.value] : [] })}
            data-testid="ct-incident-affected">
            <option value="">Select…</option>
            {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Mitigation</div>
          <textarea className="input min-h-[70px]" value={f.mitigation} onChange={(e) => setF({ ...f, mitigation: e.target.value })} data-testid="ct-incident-mitigation" />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="ct-incident-save">Open incident</button>
      </div>
    </div>
  );
}

function IncidentRow({ inc, expanded, onToggle, onChanged }) {
  const setStatus = async (status) => {
    try { await api.patch(`/control-tower/incidents/${inc.id}`, { status }); toast.success("Updated"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const total = ["shipments", "pickups", "orders", "risks"].reduce((s, k) => s + ((inc.linked || {})[k]?.length || 0), 0);
  return (
    <div className="p-3">
      <button onClick={onToggle} className="w-full text-left flex items-center gap-2" data-testid={`ct-incident-row-${inc.incident_number}`}>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <span className="text-sm font-mono text-slate-900">{inc.incident_number}</span>
        <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + sevChip(inc.severity)}>{inc.severity}</span>
        <span className="text-sm text-slate-800 truncate">{inc.title}</span>
        <span className="ml-auto text-[10px] text-slate-500">{inc.status}</span>
        <span className="text-[10px] text-slate-500"><Link2 className="h-3 w-3 inline" /> {total}</span>
      </button>
      {expanded && (
        <div className="mt-2 pl-6 space-y-2 text-xs text-slate-600">
          <div>Kind: <span className="text-slate-900">{inc.kind}</span> · Owner: <span className="text-slate-900">{inc.owner || "—"}</span> · Started: {new Date(inc.started_at).toLocaleString()}</div>
          <div>Mitigation: <span className="text-slate-900">{inc.mitigation || "—"}</span></div>
          <div>Customer impact: <span className="text-slate-900">{inc.customer_impact || "—"}</span></div>
          <div>Linked → shipments {inc.linked?.shipments?.length || 0} · pickups {inc.linked?.pickups?.length || 0} · orders {inc.linked?.orders?.length || 0} · risks {inc.linked?.risks?.length || 0}</div>
          <div className="pt-1 flex gap-1 flex-wrap">
            {["investigating", "mitigating", "monitoring", "resolved"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} className="text-[11px] px-2 py-0.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50" data-testid={`ct-incident-status-${inc.id}-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function sevChip(s) {
  return s === "critical" ? "bg-rose-100 text-rose-800 border-rose-200" :
         s === "high"     ? "bg-orange-100 text-orange-800 border-orange-200" :
         s === "medium"   ? "bg-amber-100 text-amber-800 border-amber-200" :
                             "bg-slate-100 text-slate-700 border-slate-200";
}

/* ============================================================ Alert Rules */
function AlertRulesTab({ tick, onLoaded }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/control-tower/alert-rules").then(({ data }) => { setItems(data.items || []); onLoaded?.(new Date().toISOString()); }), [onLoaded]);
  useEffect(() => { load().catch(() => {}); }, [load, tick]);
  const toggle = async (r) => {
    try { await api.patch(`/control-tower/alert-rules/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="ct-alerts-tab">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="ct-alert-new">
          <Plus className="h-4 w-4" /> New rule
        </button>
      </div>
      {showForm && <AlertRuleForm onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Rule</th><th className="text-left py-2 px-3">Kind</th><th className="text-right py-2 px-3">Threshold</th><th className="text-left py-2 px-3">Severity</th><th className="text-left py-2 px-3">Active</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No alert rules yet.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="py-2 px-3 text-slate-900">{r.name}</td>
                <td className="py-2 px-3 text-slate-600">{r.kind}</td>
                <td className="py-2 px-3 text-right tabular-nums">{r.threshold} <span className="text-slate-500 text-xs">{r.unit}</span></td>
                <td className="py-2 px-3"><span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + sevChip(r.severity)}>{r.severity}</span></td>
                <td className="py-2 px-3">
                  <button onClick={() => toggle(r)} className={"text-xs px-2 py-0.5 rounded border " + (r.active ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600")} data-testid={`ct-alert-toggle-${r.id}`}>
                    {r.active ? "Active" : "Inactive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function AlertRuleForm({ onCancel, onCreated }) {
  const [f, setF] = useState({ name: "", kind: "shipment_stale", threshold: 24, unit: "hours", severity: "medium", active: true, description: "" });
  const save = async () => {
    try {
      await api.post("/control-tower/alert-rules", f);
      toast.success("Rule created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="ct-alert-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ct-alert-name" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="ct-alert-kind">
            <option value="shipment_stale">Shipment no update</option>
            <option value="pickup_failed_repeat">Pickup failed repeatedly</option>
            <option value="wipe_pending_hours">Wipe pending too long</option>
            <option value="quarantine_days">Asset in quarantine</option>
            <option value="sla_low">SLA nearly out</option>
            <option value="approval_stale">Customer approval stale</option>
            <option value="integration_failed">Integration failed</option>
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Threshold</div>
          <input type="number" className="input" value={f.threshold} onChange={(e) => setF({ ...f, threshold: Number(e.target.value) })} data-testid="ct-alert-threshold" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Unit</div>
          <select className="input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}>
            <option value="hours">hours</option><option value="days">days</option><option value="attempts">attempts</option><option value="percent">%</option>
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Severity</div>
          <select className="input" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} data-testid="ct-alert-severity">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
          </select>
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="ct-alert-save">Create rule</button>
      </div>
    </div>
  );
}

function Skeleton() {
  return <div className="bg-white border border-slate-200 rounded-md p-8 text-sm text-slate-500 animate-pulse">Loading control tower…</div>;
}

/* ============================================================ Ask ONE */
function AskOne({ scope }) {
  const [q, setQ] = useState("");
  const [ans, setAns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const loadHistory = () => api.get("/control-tower/ask/history", { params: { limit: 20 } })
    .then(({ data }) => setHistory(data.items || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const ask = async () => {
    if (!q || q.trim().length < 4) { toast.error("Ask a real question"); return; }
    setBusy(true); setAns(null);
    try {
      const { data } = await api.post("/control-tower/ask", {
        question: q, master_company_id: scope.master_company_id, country: scope.country,
      });
      setAns(data);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ask ONE failed");
    } finally { setBusy(false); }
  };
  const replay = async (id) => {
    try { const { data } = await api.get(`/control-tower/ask/history/${id}`); setAns({ ...data, note: "Replayed from history." }); setQ(data.question); }
    catch { toast.error("Could not load history entry"); }
  };

  const suggestions = [
    "How many shipments are still in transit right now?",
    "Are there any invoices outstanding I should chase?",
    "Give me a 30-second brief on today's operations.",
    "Where should the ops team focus in the next hour?",
  ];

  return (
    <div className="space-y-4" data-testid="ct-ask">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500 flex items-center gap-1">
          <Compass className="h-3.5 w-3.5" /> Ask ONE · Operational brief
        </div>
        <div className="mt-2 flex items-start gap-2">
          <textarea
            className="input flex-1 min-h-[70px]"
            placeholder="Ask a natural-language question about your live estate…"
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="ct-ask-input" />
          <button onClick={ask} disabled={busy}
            className="h-10 px-4 rounded-md bg-slate-900 text-white text-sm disabled:opacity-60"
            data-testid="ct-ask-submit">
            {busy ? "Thinking…" : "Ask ONE"}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {suggestions.map((s) => (
            <button key={s} onClick={() => setQ(s)}
              className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">{s}</button>
          ))}
        </div>
      </div>
      {ans && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="ct-ask-answer">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Answer</div>
          <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-800 leading-6">{ans.answer}</pre>
          <details className="mt-3">
            <summary className="cursor-pointer text-[11px] uppercase tracking-widest text-slate-500">Evidence used ({Object.keys(ans.evidence || {}).length} keys)</summary>
            <pre className="mt-2 text-[11px] bg-slate-50 border border-slate-100 rounded p-2 overflow-auto max-h-64">{JSON.stringify(ans.evidence, null, 2)}</pre>
          </details>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-500">{ans.note} · Model {ans.model}</div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="ct-ask-history">
        <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">Recent questions ({history.length})</div>
        {history.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No history yet — ask a question above.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map((h) => (
              <li key={h.id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={() => replay(h.id)} data-testid={`ct-ask-h-${h.id}`}>
                <div className="text-sm text-slate-900 line-clamp-1">{h.question}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                  {h.asked_at?.slice(0, 16).replace("T", " ")} · {h.user_email}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
